/**
 * The arithmetic behind the rendered yarn.
 *
 * Every path string the swatches draw is built here, as plain functions over plain
 * numbers, and nothing in this file knows what SVG is beyond the fact that a path
 * is text. That split is worth the extra module: the two components that use it are
 * then thin enough to read in one screen, and the part that can actually be wrong,
 * namely how many stitches a fingering weight fabric shows against an aran one, is
 * checked in `geometry.test.ts` without rendering anything.
 *
 * ### Why the geometry comes off the catalogue figures
 *
 * A swatch that looked the same for every yarn would be decoration. These come out
 * of the same two numbers the rest of the page prints: the stitch gauge sets how
 * thick the strand is drawn and how fine the knitted fabric is, and the row gauge
 * sets how many courses fit in the same square. So a fingering weight at 28
 * stitches over four inches draws a visibly finer fabric than an aran at 16, and it
 * does so because the data says it should, not because somebody tuned it.
 *
 * ### Why not three.js
 *
 * A twisted strand under a real light was the obvious reach, and this page does not
 * take it. Two reasons. It is usually painted inside an iframe on the workbench,
 * seven cards at once, and seven WebGL contexts on a page that is already running
 * another app is a bad trade for a catalogue. And an SVG filter gives genuine
 * diffuse and specular lighting over a turbulence bump map, which is most of what a
 * lit fibre needs, at the cost of a few hundred bytes and no context at all. If
 * this page ever grows a single large hero strand rather than seven small ones,
 * that is the moment to reconsider.
 */

/** Two decimals is enough for a 320 unit viewBox, and it keeps paths legible. */
const round2 = (value: number): number => Math.round(value * 100) / 100;

const clamp = (value: number, low: number, high: number): number =>
  Math.min(high, Math.max(low, value));

export interface Point {
  readonly x: number;
  readonly y: number;
}

/**
 * A smooth cubic path through every one of the given points.
 *
 * Catmull-Rom control points converted to beziers, which is the cheap way to get a
 * curve that actually passes through its points rather than near them. The strand
 * is drawn along one of these, so the points below are the shape of the yarn and
 * the maths here is only what joins them up.
 *
 * Fewer than two points is not a curve, so it comes back as an empty string rather
 * than as a path with a move and nothing after it. A one point path renders as
 * nothing either way, and an empty `d` says so.
 */
export const smoothPath = (points: ReadonlyArray<Point>): string => {
  if (points.length < 2) return '';

  const at = (index: number): Point => {
    const bounded = clamp(index, 0, points.length - 1);
    // The clamp above guarantees this, and the non null assertion is the only way
    // to say so under noUncheckedIndexedAccess.
    return points[bounded] as Point;
  };

  const parts: Array<string> = [`M ${round2(at(0).x)} ${round2(at(0).y)}`];

  for (let index = 0; index < points.length - 1; index += 1) {
    const before = at(index - 1);
    const start = at(index);
    const end = at(index + 1);
    const after = at(index + 2);

    const c1 = {
      x: start.x + (end.x - before.x) / 6,
      y: start.y + (end.y - before.y) / 6,
    };
    const c2 = { x: end.x - (after.x - start.x) / 6, y: end.y - (after.y - start.y) / 6 };

    parts.push(
      `C ${round2(c1.x)} ${round2(c1.y)} ${round2(c2.x)} ${round2(c2.y)} ${round2(end.x)} ${round2(end.y)}`,
    );
  }

  return parts.join(' ');
};

// ---------- the strand ----------

/** The box the strand is drawn in. Wide and shallow, like a length of yarn. */
export const STRAND_BOX = Object.freeze({ width: 340, height: 132 });

/**
 * The line the strand runs along.
 *
 * A shallow double bend rather than a straight line, because a straight length of
 * yarn is a length of dowel. It leaves and enters past the edges of the box so the
 * strand is a piece of something longer rather than a sausage lying in a frame.
 */
export const STRAND_SPINE: ReadonlyArray<Point> = Object.freeze([
  { x: -18, y: 84 },
  { x: 74, y: 52 },
  { x: 168, y: 76 },
  { x: 262, y: 46 },
  { x: 358, y: 66 },
]);

/**
 * How thick to draw the strand, from the stitch gauge.
 *
 * Inverted, because a coarser yarn takes fewer stitches to the inch: an aran at 16
 * comes out fat and a fingering at 28 comes out fine. The bounds keep a yarn from
 * either side of the range the catalogue stocks from filling the box or vanishing
 * in it, and the linear middle is honest enough for a drawing.
 */
export const strandWidth = (stsPer4in: number): number =>
  round2(clamp(62 - stsPer4in, 26, 46));

/** The two sets of strokes that make a strand read as twisted rather than round. */
export interface Twist {
  /** The grooves between plies, dark, drawn first. */
  readonly grooves: string;
  /** The lit crown of each ply, drawn over the grooves. */
  readonly sheens: string;
  /** Stroke widths, so the component does not have to guess a ratio. */
  readonly grooveWidth: number;
  readonly sheenWidth: number;
  /** How far apart two grooves sit, kept for the tests and the lighting scale. */
  readonly pitch: number;
}

/**
 * The twist, as two multi subpath strings.
 *
 * One path element per set rather than one per stroke. Seven cards on a page, each
 * with a strand and a knitted swatch, adds up fast, and there is nothing an
 * individual groove needs to be addressable for.
 *
 * The strokes lean rather than standing upright, and they bow slightly on the way
 * down, which is what a helix looks like from the side. Pitch comes off the ply
 * count: the same thickness split into four plies gives narrower plies and a
 * tighter twist than the same thickness split into two.
 */
export const twist = (width: number, plies: number): Twist => {
  const bounded = clamp(Math.round(plies), 2, 6);
  const pitch = round2(clamp((width / bounded) * 1.7, 10, 30));
  const lean = round2(STRAND_BOX.height * 0.46);

  const strokeAt = (x: number): string =>
    `M ${round2(x)} 0 Q ${round2(x + lean * 0.34)} ${round2(STRAND_BOX.height / 2)} ${round2(x + lean)} ${STRAND_BOX.height}`;

  const grooves: Array<string> = [];
  const sheens: Array<string> = [];
  for (let x = -lean - pitch; x < STRAND_BOX.width + pitch; x += pitch) {
    grooves.push(strokeAt(x));
    // Just off the groove, on the side the light comes from, so each ply gets a
    // crown rather than the whole strand getting one stripe.
    sheens.push(strokeAt(x + pitch * 0.44));
  }

  return {
    grooves: grooves.join(' '),
    sheens: sheens.join(' '),
    grooveWidth: round2(pitch * 0.34),
    sheenWidth: round2(pitch * 0.15),
    pitch,
  };
};

// ---------- the knitted swatch ----------

/** The knitted swatch is square, and it stands for two inches of fabric. */
export const SWATCH_BOX = 132;

/** Two inches of fabric, which is the smallest piece that reads as knitting. */
export const SWATCH_INCHES = 2;

export interface StitchRow {
  /** The legs of every stitch in the row, as one path. */
  readonly legs: string;
  /** The lit crown of the same legs, drawn over them. */
  readonly sheen: string;
}

export interface StitchGrid {
  readonly columns: number;
  readonly rows: number;
  readonly stitchWidth: number;
  readonly stitchHeight: number;
  readonly legWidth: number;
  readonly sheenWidth: number;
  /**
   * The courses in draw order, bottom of the fabric first.
   *
   * Stockinette interlocks: the legs of a stitch come up over the head of the
   * stitch below it. Drawing from the bottom up means the later course paints over
   * the earlier one and the overlap falls the right way round without any masking.
   */
  readonly courses: ReadonlyArray<StitchRow>;
}

/**
 * A square of stockinette at the given gauge.
 *
 * Both counts are halved because the square is two inches rather than four, and
 * both are bounded: under about six stitches across, a grid stops reading as
 * fabric and starts reading as a fence, and over about twenty the legs are thinner
 * than a hairline and it turns to grey. Every yarn the catalogue stocks lands well
 * inside those bounds, so the clamps are there for the yarn somebody adds later.
 */
export const stitchGrid = (stsPer4in: number, rowsPer4in: number): StitchGrid => {
  const columns = clamp(Math.round((stsPer4in / 4) * SWATCH_INCHES), 6, 20);
  const rowCount = clamp(Math.round((rowsPer4in / 4) * SWATCH_INCHES), 8, 26);

  const stitchWidth = round2(SWATCH_BOX / columns);
  const stitchHeight = round2(SWATCH_BOX / rowCount);

  const built: Array<StitchRow> = [];
  for (let row = rowCount - 1; row >= 0; row -= 1) {
    const top = row * stitchHeight;
    const legs: Array<string> = [];
    const sheen: Array<string> = [];

    for (let column = 0; column < columns; column += 1) {
      const left = column * stitchWidth;
      const middle = left + stitchWidth / 2;
      const foot = top + stitchHeight;
      // The legs start above their own cell so they cross the head of the row
      // beneath and the fabric interlocks instead of stacking.
      const head = top - stitchHeight * 0.34;

      const leg = (from: number): string =>
        `M ${round2(from)} ${round2(head)} C ${round2(from + (middle - from) * 0.34)} ${round2(top + stitchHeight * 0.44)} ${round2(from + (middle - from) * 0.72)} ${round2(top + stitchHeight * 0.76)} ${round2(middle)} ${round2(foot)}`;

      legs.push(leg(left), leg(left + stitchWidth));
      sheen.push(leg(left), leg(left + stitchWidth));
    }

    built.push({ legs: legs.join(' '), sheen: sheen.join(' ') });
  }

  return {
    columns,
    rows: rowCount,
    stitchWidth,
    stitchHeight,
    legWidth: round2(stitchWidth * 0.46),
    sheenWidth: round2(stitchWidth * 0.15),
    courses: built,
  };
};

// ---------- texture ----------

/**
 * A stable turbulence seed from a yarn id.
 *
 * The fibre texture is noise, and noise needs a seed. Taking it from the id rather
 * than from a random call means a card looks the same on every load and in every
 * screenshot, which is the difference between a texture and a flicker. Any small
 * spread of integers will do, so this is the plainest hash that gives one.
 */
export const textureSeed = (id: string): number => {
  let total = 7;
  for (const character of id) {
    total = (total * 31 + character.charCodeAt(0)) % 9973;
  }
  return total % 90;
};
